import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import { format } from "date-fns";

import FormikInput from "@components/form/FormikInput";
import FormikDatepicker from "@components/form/FormikDatepicker";
import FormikRadioGroup from "@components/form/FormikRadioGroup";
import { saveRate } from "@service/BillRateService";
import saveBillRateSchema from "@validators/saveBillRateSchema";
import  Button  from "@components/Button";
import { IUpdateBillingRateDTO } from "../../interfaces/IUpdateBillingRateDTO";
import { SnackbarContext } from '@components/constant_components/providers/SnackbarProvider';
import Spinner from "@components/Spinner";
interface IBillRateFieldsProps {
  worker_id: number;
}

interface iFormBillRateValues {
  billrate_start_date: Date;
  billrate_type: 'hourly' | 'weekly' | 'daily'
  billrate_rate: string;
}

export function BillRateFields({ worker_id }: IBillRateFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const { triggerSnackbar } = useContext(SnackbarContext);

  const initialBillRateValues: iFormBillRateValues = {
    billrate_start_date: new Date(),
    billrate_rate: '',
    billrate_type: 'hourly',
  };

  const billrateValidationSchema = saveBillRateSchema(t);

  const handleBillRateSubmit = useCallback(
    async (values: iFormBillRateValues,
       { setSubmitting }: import("formik").FormikHelpers<iFormBillRateValues>
    ) => {
      try {
        const data = {
          worker_id: worker_id,
          value: values.billrate_rate,
          rate_type: values.billrate_type,
          start_date:  format(values.billrate_start_date, "yyyy-MM-dd"),
        } as IUpdateBillingRateDTO;
        await saveRate(data);
        triggerSnackbar('Bill rate updated successfully!');
      } catch (err) {
        triggerSnackbar('Error updating bill rate!');
      } finally {
         setSubmitting(false);
      }
    },
    [worker_id, triggerSnackbar]
  );

  return (
    <Formik<iFormBillRateValues>
      initialValues={initialBillRateValues}
      validationSchema={billrateValidationSchema}
      onSubmit={handleBillRateSubmit}
      >
      {({ errors, touched, isSubmitting }) => (
        <div className="tw-px-6 tw-pb-6" data-testid="form">
          <Form>
              <div className="tw-border tw-p-4 tw-border-gray-500">
                <span className="tw-text-sm tw-bg-gray-400 tw-p-1 tw-rounded">BILL RATE</span>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-2">
                  <Field
                    name="billrate_start_date"
                    label={t("Rate.start_date")}
                    as={FormikDatepicker}
                    error={
                      touched.billrate_start_date && errors.billrate_start_date
                    }
                    />
                  <Field
                    name="billrate_type"
                    label={t("Rate.type")}
                    options={[
                      { value: "hourly", label: t("Rate.hourly") },
                      { value: "daily", label: t("Rate.daily") },
                      { value: "weekly", label: t("Rate.weekly") },
                    ]}
                    as={FormikRadioGroup}
                    error={touched.billrate_type && errors.billrate_type}
                  />
                  <Field
                    name="billrate_rate"
                    label={t("Rate.rate")}
                    as={FormikInput}
                    inputMode="numeric"
                    error={touched.billrate_rate && errors.billrate_rate}
                  />
                </div>
                <div className="tw-flex tw-grid-cols-1 md:tw-grid-cols-1 tw-justify-end tw-mt-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="md:tw-w-[30%]"
                    >
                    {isSubmitting ? <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" /> : 'Bill Rate Update'}
                  </Button>
                </div>
              </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}